import type { EstadoInstancia } from './api/posVendaApi';

/**
 * Estado do WhatsApp do pós-venda compartilhado dentro da aba/janela.
 *
 * O indicador de celular da topbar e a tela de conexão descobrem o estado
 * por caminhos diferentes: a topbar consulta a cada 60s, a tela de conexão
 * faz polling curto enquanto o QR está visível. Sem um lugar comum, quem
 * lia o QR via a tela dizer "Conectado" e o ícone da topbar continuava
 * riscado até o próximo minuto — o sintoma clássico de "conectou e o
 * ícone não mudou".
 *
 * Isto NÃO substitui o WebSocket: o socket avisa as outras máquinas e
 * abas; este store alinha o que já está na mesma tela.
 */

export type EstadoWhatsapp = {
  /** null enquanto ninguém consultou ainda. */
  conectado: boolean | null;
  numero: string | null;
};

let atual: EstadoWhatsapp = { conectado: null, numero: null };
const ouvintes = new Set<(e: EstadoWhatsapp) => void>();

export function obterEstadoWhatsapp(): EstadoWhatsapp {
  return atual;
}

/** Publica o estado. Não notifica quando nada mudou de fato. */
export function publicarEstadoWhatsapp(estado: EstadoWhatsapp): void {
  if (atual.conectado === estado.conectado && atual.numero === estado.numero) return;
  atual = estado;
  for (const ouvinte of ouvintes) {
    try {
      ouvinte(atual);
    } catch {
      // Um ouvinte quebrado não pode impedir os outros de receber.
    }
  }
}

/** Conveniência para quem tem a resposta crua de `/api/pos-venda/instancia`. */
export function publicarEstadoInstancia(estado: EstadoInstancia | { status: string; numero: string | null }): void {
  publicarEstadoWhatsapp({
    conectado: estado.status === 'conectada',
    numero: estado.numero,
  });
}

export function assinarEstadoWhatsapp(ouvinte: (e: EstadoWhatsapp) => void): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

/** Logout/troca de conta: o próximo login recomeça sem herdar o anterior. */
export function limparEstadoWhatsapp(): void {
  atual = { conectado: null, numero: null };
}
